import html2canvas from 'html2canvas';
import { getShareCardSpec, type ShareCardAspect } from './shareCardHelpers';
import { waitForCardQrImage } from './quizQr';

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  width: number,
  height: number
): void {
  const imgRatio = img.naturalWidth / img.naturalHeight;
  const boxRatio = width / height;
  let sx = 0;
  let sy = 0;
  let sw = img.naturalWidth;
  let sh = img.naturalHeight;

  if (imgRatio > boxRatio) {
    sw = img.naturalHeight * boxRatio;
    sx = (img.naturalWidth - sw) / 2;
  } else {
    sh = img.naturalWidth / boxRatio;
    sy = (img.naturalHeight - sh) / 2;
  }

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
}

/** html2canvas không render object-fit:cover đúng — rasterize avatar trước khi chụp */
async function rasterizeCoverAvatars(root: HTMLElement): Promise<void> {
  const frames = Array.from(root.querySelectorAll('[data-share-avatar-frame]'));

  for (const frame of frames) {
    const img = frame.querySelector('[data-share-avatar-img]') as HTMLImageElement | null;
    if (!img?.src || !img.complete || !img.naturalWidth) continue;

    const width = Math.round(frame.clientWidth);
    const height = Math.round(frame.clientHeight);
    if (!width || !height) continue;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;

      drawImageCover(ctx, img, width, height);
      img.src = canvas.toDataURL('image/png');
      img.style.objectFit = 'fill';
      img.style.objectPosition = 'center';
      img.style.width = '100%';
      img.style.height = '100%';
    } catch {
      // CORS hoặc ảnh ngoài — giữ object-fit trên preview
    }
  }
}

async function waitForImages(container: HTMLElement): Promise<void> {
  const imgs = Array.from(container.querySelectorAll('img'));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
}

function syncCloneImages(source: HTMLElement, clone: HTMLElement): void {
  const sourceImgs = source.querySelectorAll('img');
  const cloneImgs = clone.querySelectorAll('img');
  cloneImgs.forEach((img, index) => {
    const original = sourceImgs[index] as HTMLImageElement | undefined;
    if (!original?.src) return;
    const clonedImg = img as HTMLImageElement;
    clonedImg.src = original.src;
    if (original.crossOrigin) clonedImg.crossOrigin = original.crossOrigin;
  });
}

/**
 * Clone card ra ngoài modal để tránh backdrop-filter/overlay của quiz-modal
 * bị html2canvas hòa trộn thành lớp phủ tối trên ảnh xuất.
 */
export async function captureShareCard(
  cardEl: HTMLElement,
  cardId: string,
  aspect: ShareCardAspect = 'story'
): Promise<HTMLCanvasElement> {
  await document.fonts.ready;
  await waitForCardQrImage(cardEl);

  const { width: targetWidth, height: targetHeight } = getShareCardSpec(aspect);

  const clone = cardEl.cloneNode(true) as HTMLElement;
  clone.id = `${cardId}-capture`;
  clone.style.transform = 'none';
  clone.style.boxShadow = 'none';
  clone.style.margin = '0';
  clone.style.position = 'relative';
  clone.style.left = '0';
  clone.style.top = '0';
  clone.style.width = `${targetWidth}px`;
  clone.style.height = `${targetHeight}px`;

  syncCloneImages(cardEl, clone);

  const mount = document.createElement('div');
  mount.setAttribute('aria-hidden', 'true');
  mount.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${targetWidth}px`,
    `height:${targetHeight}px`,
    'overflow:visible',
    'pointer-events:none',
    'z-index:-1',
  ].join(';');

  mount.appendChild(clone);
  document.body.appendChild(mount);

  try {
    await waitForImages(clone);
    await rasterizeCoverAvatars(clone);
    await new Promise((resolve) => setTimeout(resolve, 150));

    const canvas = await html2canvas(clone, {
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#0a0a0a',
      scale: 1,
      width: targetWidth,
      height: targetHeight,
      windowWidth: targetWidth,
      windowHeight: targetHeight,
      logging: false,
    });

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      const normalized = document.createElement('canvas');
      normalized.width = targetWidth;
      normalized.height = targetHeight;
      const ctx = normalized.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#0a0a0a';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
      }
      return normalized;
    }

    return canvas;
  } finally {
    mount.remove();
  }
}